import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";

interface Props {
  children: ReactNode;
  /** What failed, in words the user understands. */
  title?: string;
}

interface State {
  error: Error | null;
}

/**
 * Keeps one broken component from taking down the page.
 *
 * A PDF that will not render, or a chart that throws, should leave the rest of
 * the screen usable rather than replacing it with a stack trace.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged for the browser console; the user gets the message below.
    console.error("Component failed:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Alert title={this.props.title ?? "This part of the page could not load"}>
          <p className="mt-1">
            Refresh the page. If it keeps happening, tell HR what you were doing.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </Button>
        </Alert>
      );
    }
    return this.props.children;
  }
}
